import { useEffect, useRef, type CSSProperties } from 'react';
import { prefersReducedMotion } from '../../motion';
import type { LayerDelivery, LayerSettings } from '../types';

/** Společné props všech povrchů. */
export interface SurfaceProps {
  readonly layer: LayerDelivery;
  readonly onClose: () => void;
  readonly onInteract: (kind: 'click' | 'submit') => void;
  readonly onSubmitted: () => void;
}

/** Landmarky, které se při otevřeném modálu schovají před AT (`aria-hidden`). */
const BG_LANDMARKS = ['.header-sticky', '.service-bar', 'main.page', '.footer'];

function clampWidth(px: number | undefined): number {
  return Math.max(280, Math.min(720, px ?? 420));
}

/** Barvy a šířka z nastavení dokumentu. */
export function cardStyle(settings: LayerSettings, withWidth: boolean): CSSProperties {
  const style: CSSProperties = {};
  if (withWidth) style.width = `min(${clampWidth(settings.widthPx)}px, calc(100vw - 2rem))`;
  if (settings.backgroundColor !== undefined) style.background = settings.backgroundColor;
  if (settings.textColor !== undefined) style.color = settings.textColor;
  return style;
}

/** Třída animace; `prefers-reduced-motion` ji vždy vypne. */
export function animationClass(settings: LayerSettings): string {
  if (prefersReducedMotion()) return 'layer-anim-none';
  return `layer-anim-${settings.animation ?? 'fade'}`;
}

function focusable(node: HTMLElement): HTMLElement[] {
  const selector =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  return Array.from(node.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * Chování modálního dialogu: focus trap, Escape, scroll-lock, `aria-hidden`
 * na pozadí a NÁVRAT fokusu na spouštěč po zavření. Vrací ref na kontejner
 * dialogu (musí mít `tabIndex={-1}`).
 */
export function useModalDialog(onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const node = ref.current;
    if (node === null) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const landmarks = BG_LANDMARKS.flatMap((selector) =>
      Array.from(document.querySelectorAll<HTMLElement>(selector)),
    );
    for (const el of landmarks) el.setAttribute('aria-hidden', 'true');

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const list = focusable(node);
    (list[0] ?? node).focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable(node);
      if (items.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = previousOverflow;
      for (const el of landmarks) el.removeAttribute('aria-hidden');
      previouslyFocused?.focus?.();
    };
  }, []);

  return ref;
}

/**
 * Odlehčené chování NEmodálních povrchů (slide-in, bar, floating): Escape
 * zavírá, ale žádný scroll-lock ani focus trap (nepřerušují nákup).
 */
export function useDismissible(onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

/** Tlačítko zavření (×). */
export function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className="layer-close" aria-label="Zavřít" onClick={onClose}>
      ×
    </button>
  );
}
